import { TableSettingsFieldsTabType, TableSettingsTabType } from '@doss/common-all'
import { useEffect, useState } from 'react'
import { RxCopy, RxPlus } from 'react-icons/rx'
import { useSearchParams } from 'react-router'
import { Button } from '~/@/components/ui/button'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { SettingsPageTable } from '~/components/tables/types'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'
import { getFieldsTabFromQuery, getTabFromQuery } from './utils'

interface TableSettingsTitleBarActionsProps {
  table: SettingsPageTable | null | undefined
}

/**
 * Copy Schema and Add Field title-bar actions for the Fields tab; clicks are relayed to
 * TableSettingsFields (a different React tree) via DossSignals
 */
export const TableSettingsTitleBarActions = ({ table }: TableSettingsTitleBarActionsProps) => {
  const { t } = useTypedTranslation()
  const { isInternal } = useGlobalContext()
  const { subscribe, unsubscribe, dispatch } = useDossSignals()
  const [searchParams] = useSearchParams()
  const [actionsDisabled, setActionsDisabled] = useState(false)
  const [copiedOpen, setCopiedOpen] = useState(false)

  const settingsTab = getTabFromQuery(searchParams.get('tab') ?? undefined)
  const rawFieldsTab = getFieldsTabFromQuery(searchParams.get('fields') ?? undefined)
  const groupId = searchParams.get('groupId') ?? undefined

  // Mirror TableSettingsFields' hidden-tab remapping (child-table page / non-internal user)
  const isHiddenTab =
    (rawFieldsTab === TableSettingsFieldsTabType.CHILD_TABLES && table?.parentTable != null) ||
    (rawFieldsTab === TableSettingsFieldsTabType.COMPOSITE_KEYS && !isInternal)
  const fieldsTab = isHiddenTab ? TableSettingsFieldsTabType.ALL_FIELDS : rawFieldsTab

  // Disable these actions while the fields multi-select action bar or the right panel is open
  useEffect(() => {
    const subscriptionId = subscribe(
      DossSignalType.TABLE_SETTINGS_ACTIONS_DISABLED_CHANGED,
      async (disabled?: boolean) => {
        setActionsDisabled(disabled === true)
      },
    )
    return () => {
      unsubscribe(subscriptionId)
    }
  }, [subscribe, unsubscribe])

  if (settingsTab !== TableSettingsTabType.FIELDS) {
    return null
  }

  const showCopySchema = fieldsTab === TableSettingsFieldsTabType.ALL_FIELDS
  const showAddField =
    fieldsTab !== TableSettingsFieldsTabType.GROUPS &&
    fieldsTab !== TableSettingsFieldsTabType.COMPOSITE_KEYS

  return (
    <div className="flex items-center gap-2">
      {showCopySchema && (
        <Tooltip
          enterDelay={0}
          onClose={() => setCopiedOpen(false)}
          open={copiedOpen}
          title={t('common.copied')}
        >
          <Button
            disabled={actionsDisabled}
            onClick={() => {
              // Optimistic tooltip; the copy no-ops only before the grid registers its getter
              dispatch(DossSignalType.COPY_TABLE_SCHEMA)
              setCopiedOpen(true)
              setTimeout(() => setCopiedOpen(false), 750)
            }}
            size="xs"
            startIcon={<RxCopy />}
            variant="ghost"
          >
            {t('table_settings.copy_schema')}
          </Button>
        </Tooltip>
      )}
      {showAddField && (
        <Button
          disabled={
            actionsDisabled || (fieldsTab === TableSettingsFieldsTabType.CHILD_TABLES && !groupId)
          }
          onClick={() => dispatch(DossSignalType.OPEN_CREATE_FIELD_PANEL)}
          size="xs"
          startIcon={<RxPlus />}
          variant="primary"
        >
          {t('table_settings.group.add_field')}
          <span className="ms-1 text-[9px] font-normal opacity-70">N</span>
        </Button>
      )}
    </div>
  )
}
